import React from 'react';
import AppDropdown from '../../../../components/AppDropdown';

export default function LeaveRuleDetailModal({
  isOpen,
  isEdit,
  ruleName,
  form,
  isSaving,
  leaveTypeOptions = [],
  genderOptions = [],
  accumulateOptions = [],
  onPatch,
  onSave,
  onClose,
}) {
  if (!isOpen) return null;

  const dropdownLeaveTypes = leaveTypeOptions.map((opt) => ({
    value: String(opt.value),
    label: opt.alias ? `${opt.label} (${opt.alias})` : opt.label,
  }));

  const selectedType = leaveTypeOptions.find(
    (opt) => String(opt.value) === String(form.leaveTypeId)
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-xs p-4 animate-in fade-in duration-150">
      <div className="w-full max-w-xl overflow-hidden rounded-3xl border border-outline-variant/30 bg-surface-container-lowest shadow-2xl animate-in zoom-in-95 duration-150">
        {/* Modal Header */}
        <div className="flex items-center justify-between border-b border-outline-variant/20 bg-surface-container-low/60 px-6 py-4">
          <div>
            <h3 className="font-display text-lg font-bold text-on-surface">
              {isEdit ? 'Edit Leave Quota' : 'Add Leave Quota'}
            </h3>
            <p className="text-xs font-semibold text-primary">
              Rule: {ruleName || 'Leave Rule'}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-xl p-1.5 text-on-surface-variant hover:bg-surface-container-high transition cursor-pointer"
          >
            <span className="material-symbols-outlined text-lg">close</span>
          </button>
        </div>

        {/* Modal Form Body */}
        <div className="p-6 space-y-4 max-h-[75vh] overflow-y-auto">
          {/* Leave Type */}
          <div className="grid grid-cols-1 sm:grid-cols-[140px_1fr] items-center gap-3">
            <label className="text-xs font-bold text-on-surface-variant sm:text-right">
              Leave Type <span className="text-error">*</span>
            </label>
            <div className="flex items-center gap-2">
              {selectedType?.color && (
                <span
                  className="h-3.5 w-3.5 shrink-0 rounded-full border border-black/10 shadow-xs"
                  style={{ backgroundColor: selectedType.color }}
                  title={selectedType.label}
                />
              )}
              <div className="flex-1">
                <AppDropdown
                  options={dropdownLeaveTypes}
                  value={String(form.leaveTypeId || '')}
                  onChange={(val) => onPatch('leaveTypeId', val)}
                  placeholder="Select Leave Type"
                  triggerClassName="h-9 w-full bg-surface-container-low text-xs border border-outline-variant/30 rounded-lg px-3"
                />
              </div>
            </div>
          </div>

          {/* Applicable Gender */}
          <div className="grid grid-cols-1 sm:grid-cols-[140px_1fr] items-center gap-3">
            <label className="text-xs font-bold text-on-surface-variant sm:text-right">
              Gender <span className="text-error">*</span>
            </label>
            <AppDropdown
              options={genderOptions}
              value={form.gender || 'A'}
              onChange={(val) => onPatch('gender', val)}
              placeholder="Select Gender"
              triggerClassName="h-9 w-full bg-surface-container-low text-xs border border-outline-variant/30 rounded-lg px-3"
            />
          </div>

          {/* Total Days */}
          <div className="grid grid-cols-1 sm:grid-cols-[140px_1fr] items-center gap-3">
            <label className="text-xs font-bold text-on-surface-variant sm:text-right">
              Total Days <span className="text-error">*</span>
            </label>
            <input
              type="number"
              min={0}
              step="0.5"
              value={form.totalDays}
              onChange={(e) => onPatch('totalDays', e.target.value)}
              className="h-9 w-full rounded-lg border border-outline-variant/30 bg-surface-container-low px-3 text-xs text-on-surface outline-none transition focus:border-primary focus:ring-2 focus:ring-primary/20"
            />
          </div>

          {/* Min Days */}
          <div className="grid grid-cols-1 sm:grid-cols-[140px_1fr] items-center gap-3">
            <label className="text-xs font-bold text-on-surface-variant sm:text-right">
              Min Days
            </label>
            <input
              type="number"
              min={0}
              step="0.5"
              value={form.minDays}
              onChange={(e) => onPatch('minDays', e.target.value)}
              className="h-9 w-full rounded-lg border border-outline-variant/30 bg-surface-container-low px-3 text-xs text-on-surface outline-none transition focus:border-primary focus:ring-2 focus:ring-primary/20"
            />
          </div>

          {/* Max Days */}
          <div className="grid grid-cols-1 sm:grid-cols-[140px_1fr] items-center gap-3">
            <label className="text-xs font-bold text-on-surface-variant sm:text-right">
              Max Days
            </label>
            <input
              type="number"
              min={0}
              step="0.5"
              value={form.maxDays}
              onChange={(e) => onPatch('maxDays', e.target.value)}
              className="h-9 w-full rounded-lg border border-outline-variant/30 bg-surface-container-low px-3 text-xs text-on-surface outline-none transition focus:border-primary focus:ring-2 focus:ring-primary/20"
            />
          </div>

          {/* CF Days (Carry Forward) */}
          <div className="grid grid-cols-1 sm:grid-cols-[140px_1fr] items-center gap-3">
            <label className="text-xs font-bold text-on-surface-variant sm:text-right">
              CF Days
            </label>
            <input
              type="number"
              min={0}
              step="0.5"
              value={form.cfDays}
              onChange={(e) => onPatch('cfDays', e.target.value)}
              className="h-9 w-full rounded-lg border border-outline-variant/30 bg-surface-container-low px-3 text-xs text-on-surface outline-none transition focus:border-primary focus:ring-2 focus:ring-primary/20"
            />
          </div>

          {/* Accumulate */}
          <div className="grid grid-cols-1 sm:grid-cols-[140px_1fr] items-center gap-3">
            <label className="text-xs font-bold text-on-surface-variant sm:text-right">
              Accumulate
            </label>
            <AppDropdown
              options={accumulateOptions}
              value={form.accumulate}
              onChange={(val) => onPatch('accumulate', val)}
              triggerClassName="h-9 w-full bg-surface-container-low text-xs border border-outline-variant/30 rounded-lg px-3"
            />
          </div>

          {/* Lapse Days */}
          <div className="grid grid-cols-1 sm:grid-cols-[140px_1fr] items-center gap-3">
            <label className="text-xs font-bold text-on-surface-variant sm:text-right">
              Lapse Days
            </label>
            <input
              type="number"
              min={0}
              step="0.5"
              value={form.lapseDays}
              onChange={(e) => onPatch('lapseDays', e.target.value)}
              className="h-9 w-full rounded-lg border border-outline-variant/30 bg-surface-container-low px-3 text-xs text-on-surface outline-none transition focus:border-primary focus:ring-2 focus:ring-primary/20"
            />
          </div>

          {/* Extra Days */}
          <div className="grid grid-cols-1 sm:grid-cols-[140px_1fr] items-center gap-3">
            <label className="text-xs font-bold text-on-surface-variant sm:text-right">
              Extra Days
            </label>
            <input
              type="number"
              min={0}
              step="0.5"
              value={form.extraDays}
              onChange={(e) => onPatch('extraDays', e.target.value)}
              className="h-9 w-full rounded-lg border border-outline-variant/30 bg-surface-container-low px-3 text-xs text-on-surface outline-none transition focus:border-primary focus:ring-2 focus:ring-primary/20"
            />
          </div>

          {/* Sandwich Rule */}
          <div className="grid grid-cols-1 sm:grid-cols-[140px_1fr] items-center gap-3">
            <label className="text-xs font-bold text-on-surface-variant sm:text-right">
              Sandwich Rule
            </label>
            <label className="inline-flex items-center gap-2.5 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={Boolean(form.sandwichPresent)}
                onChange={(e) => onPatch('sandwichPresent', e.target.checked)}
                className="h-4 w-4 rounded border-outline-variant text-primary focus:ring-primary/30 cursor-pointer accent-[#0047ab]"
              />
              <span className="text-xs font-semibold text-on-surface">
                Apply Sandwich Rule (count intermediate holidays/weekoffs as leave)
              </span>
            </label>
          </div>
        </div>

        {/* Modal Footer */}
        <div className="flex items-center justify-end gap-3 border-t border-outline-variant/15 bg-surface-container-low/40 px-6 py-4">
          <button
            type="button"
            onClick={onClose}
            className="h-9 px-5 bg-surface-container-high hover:bg-surface-container-highest text-on-surface font-bold rounded-xl text-xs transition cursor-pointer border border-outline-variant/20"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={isSaving}
            onClick={onSave}
            className="h-9 px-6 bg-[#0047ab] hover:bg-[#003788] text-white font-bold rounded-xl text-xs transition shadow-xs flex items-center gap-2 cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
          >
            <span className="material-symbols-outlined text-sm">save</span>
            <span>{isSaving ? 'Saving...' : 'Save Quota'}</span>
          </button>
        </div>
      </div>
    </div>
  );
}
